import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import { Badge, Select, Switch, Text, Textarea, TextInput } from "@mantine/core";
import { IconPencil, IconTrash } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import ActionCellRender from "../../components/actionCellRender";
import PostingRuleLinesEditor from "../../components/accounting/PostingRuleLinesEditor";
import { useServerApi } from "../../hooks/userServerApi";
import { notifyListRefresh } from "../../context/tabContext";
import { POSTING_EVENT_TYPES } from "../../data/postingEventTypes";

const apiEntity = "accountingPostingRule";

const buildEventTypeSelect = (usedEventTypes) => {
  const EventTypeSelect = ({ form, formMode, ...props }) => {
    const { t } = useTranslation();
    const isEdit = Boolean(form.values?._id) || formMode === "edit";

    const data = POSTING_EVENT_TYPES.map((item) => ({
      value: item.value,
      label: t(item.label, { defaultValue: item.label }),
      disabled: !isEdit && usedEventTypes.includes(item.value),
    }));

    return (
      <Select
        {...props}
        {...form.getInputProps("eventType")}
        data={data}
        disabled={isEdit}
        searchable
      />
    );
  };
  return EventTypeSelect;
};

const buildFormSchema = (t, EventTypeSelect) => ({
  title: "accounting.postingRule.formTitle",
  titleField: "name",
  apiEntity,
  closeAfterSave: true,
  initialValues: {
    isActive: true,
    lines: [],
  },
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "xl",
  },
  layout: {
    containers: [
      {
        key: "main",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "accounting.postingRule.ruleDetails",
          description: "accounting.postingRule.ruleDetailsDesc",
        },
      },
      {
        key: "lines",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "accounting.postingRule.journalLines",
          description: "accounting.postingRule.journalLinesDesc",
        },
      },
    ],
    fields: [
      {
        name: "eventType",
        component: EventTypeSelect,
        parent: "main",
        props: {
          label: "accounting.postingRule.eventType",
          required: true,
        },
      },
      {
        name: "name",
        component: TextInput,
        parent: "main",
        props: {
          label: "Name",
          required: true,
        },
      },
      {
        name: "description",
        component: Textarea,
        parent: "main",
        props: {
          label: "Description",
          minRows: 2,
        },
      },
      {
        name: "revenueRecognition",
        component: Select,
        parent: "main",
        props: {
          label: "accounting.postingRule.revenueRecognition",
          data: [
            { value: "PAYMENT_MATCHED", label: t("accounting.postingRule.revPaymentMatched") },
            { value: "DN_CONFIRM", label: t("accounting.postingRule.revDnConfirm") },
            { value: "INSURER_SETTLEMENT", label: t("accounting.postingRule.revInsurerSettlement") },
          ],
        },
      },
      {
        name: "isActive",
        component: Switch,
        parent: "main",
        props: {
          label: "accounting.field.active",
        },
      },
      {
        name: "lines",
        component: PostingRuleLinesEditor,
        parent: "lines",
        props: {},
      },
    ],
  },
});

const buildTableSchema = (t) => ({
  columns: [
    {
      field: "eventType",
      headerName: "accounting.postingRule.eventType",
      sortable: true,
      width: 220,
    },
    {
      field: "name",
      headerName: "Name",
      sortable: true,
    },
    {
      field: "lines",
      headerName: "accounting.postingRule.lines",
      sortable: false,
      width: 90,
      cellRender: (col, data) => data.lines?.length || 0,
    },
    {
      field: "isActive",
      headerName: "accounting.field.active",
      sortable: true,
      width: 100,
      cellRender: (col, data) => (
        <Badge size="xs" color={data.isActive ? "green" : "gray"}>
          {data.isActive ? t("Yes") : t("No")}
        </Badge>
      ),
    },
    {
      field: "action",
      cellRender: ActionCellRender,
      isAction: true,
      actions: [{ name: "edit", icon: IconPencil }],
      actionMenu: [
        {
          name: "delete",
          label: "accounting.postingRule.deleteRule",
          icon: IconTrash,
          props: { color: "red" },
        },
      ],
    },
  ],
  searchableFields: ["eventType", "name", "description"],
});

const PostingRules = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [seeding, setSeeding] = useState(false);
  const [usedEventTypes, setUsedEventTypes] = useState([]);
  const location = useLocation();

  const handleFetchSuccess = useCallback((_total, docs = []) => {
    const next = docs.map((row) => row.eventType).filter(Boolean);
    setUsedEventTypes((prev) => {
      if (
        prev.length === next.length &&
        prev.every((value, index) => value === next[index])
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const EventTypeSelect = useMemo(
    () => buildEventTypeSelect(usedEventTypes),
    [usedEventTypes]
  );

  const formSchema = useMemo(
    () => buildFormSchema(t, EventTypeSelect),
    [t, EventTypeSelect]
  );
  const tableSchema = useMemo(() => buildTableSchema(t), [t]);

  const customActions = useMemo(
    () => [
      {
        label: seeding
          ? t("accounting.postingRule.seeding")
          : t("accounting.postingRule.seedDefaults"),
        variant: "default",
        disabled: seeding,
        requiresWrite: true,
        onClick: async () => {
          setSeeding(true);
          try {
            await api.Accounting.seedPostingRules();
            showNotification({
              title: t("accounting.postingRule.seedSuccessTitle"),
              message: t("accounting.postingRule.seedSuccessMessage"),
            });
            notifyListRefresh(location.pathname);
          } catch (error) {
            showNotification({
              color: "red",
              title: t("accounting.postingRule.seedFailed"),
              message: String(error?.message || error),
            });
          } finally {
            setSeeding(false);
          }
        },
      },
    ],
    [api, location.pathname, seeding, t]
  );

  return (
    <>
      <Text size="sm" color="dimmed" mb="md" px="md" pt="md">
        {t("accounting.postingRule.pageIntro")}
      </Text>
      <PageList
        title={t("accounting.modules.postingRules.label")}
        apiEntity={apiEntity}
        tableSchema={tableSchema}
        formSchema={formSchema}
        customActions={customActions}
        onFetchSuccess={handleFetchSuccess}
        initSort={{ by: "eventType", order: "asc" }}
        defaultPageSize={50}
      />
    </>
  );
};

export default PostingRules;
